/**
 * PostHog (jest-client)
 *
 * Thin wrapper over posthog-js for the Jest-hosted SPA. Events are sent to the
 * FantasyPRIME backend's `/ingest` reverse proxy (absolute URL — the app is
 * served from Jest's CDN, so a relative path would hit Jest's host). Routing
 * through the already-trusted backend domain sidesteps Jest's third-party-host
 * restriction.
 *
 * Every export no-ops when VITE_PUBLIC_POSTHOG_KEY / _HOST are unset.
 */

import posthog from 'posthog-js';
import type { User } from '@/stores/auth.store';

const KEY = import.meta.env.VITE_PUBLIC_POSTHOG_KEY as string | undefined;
const HOST = import.meta.env.VITE_PUBLIC_POSTHOG_HOST as string | undefined;
// Tags every event so prod/staging data can be separated in one project.
const ENV =
  (import.meta.env.VITE_PUBLIC_POSTHOG_ENV as string | undefined) ||
  'development';

let enabled = false;

export function initAnalytics(): void {
  if (enabled || !KEY || !HOST) return;

  posthog.init(KEY, {
    api_host: HOST,
    ui_host: 'https://us.posthog.com',
    person_profiles: 'identified_only',
    // memory router emits no History events — pageviews are fired manually by
    // RouteTracker on useLocation change.
    capture_pageview: false,
    capture_pageleave: true,
    autocapture: true,
    // avoid lazy-loading recorder assets from a third-party host inside Jest's
    // sandbox (index.html forbids external assets).
    disable_session_recording: true,
  });
  // super-property: attached to every client event (filter insights by it).
  posthog.register({ environment: ENV });
  enabled = true;
}

export function identifyUser(
  user: Pick<User, 'id' | 'email' | 'username' | 'role' | 'jestGuest'>,
): void {
  if (!enabled) return;
  posthog.identify(user.id, {
    email: user.email,
    username: user.username,
    role: user.role,
    jest_guest: user.jestGuest,
  });
}

export function resetUser(): void {
  if (!enabled) return;
  posthog.reset();
}

export function capture(
  event: string,
  properties?: Record<string, unknown>,
): void {
  if (!enabled) return;
  posthog.capture(event, properties);
}
